// utils.js — utilidades pequeñas compartidas por todas las pantallas.
import { useEffect, useState, useCallback, useRef } from "react";
import api from "./setupAxios";

// Pantalla de inicio de cada tipo de usuario.
export const INICIO = {
  alumno: "/alumno",
  admisiones: "/admisiones",
  ensombrado: "/ensombrado",
  checkin: "/checkin",
  admin: "/admin",
};

const DIAS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
export { MESES };

// "2026-09-01" -> "01/09/2026"
export const fechaES = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");
export const diaSemana = (iso) => DIAS[new Date(`${iso.slice(0, 10)}T00:00:00`).getDay()];
export const fechaLarga = (iso) => (iso ? `${diaSemana(iso)} ${fechaES(iso)}` : "");
export const fechaHoraES = (iso) => (iso ? `${fechaES(iso)} ${iso.slice(11, 16)}` : "");
export const fechaHoraSegES = (iso) => (iso ? `${fechaES(iso)} ${iso.slice(11, 19)}` : "");
export const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const primerNombre = (n) => (n || "").trim().split(/\s+/)[0] || "";

// Mensaje de error de una petición fallida (el back responde {ok:false, error}).
export const msgError = (e, def = "No se pudo completar la operación") =>
  e?.response?.data?.error || def;

// Descarga un fichero (PDF/CSV) pasando por axios para llevar el token.
export async function descargar(url, params, nombreDefecto) {
  try {
    const r = await api.get(url, { params, responseType: "blob" });
    const disp = r.headers["content-disposition"] || "";
    const m = /filename="?([^";]+)"?/i.exec(disp);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = m ? m[1] : nombreDefecto;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    return null;
  } catch (e) {
    // el error llega como blob: se lee para sacar el mensaje del back
    try {
      const txt = await e.response.data.text();
      return JSON.parse(txt).error || "No se pudo descargar";
    } catch {
      return "No se pudo descargar";
    }
  }
}

export function useDebounce(valor, ms = 350) {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

// Carga una lista del back con filtros (solo manda los que tienen valor) y la
// recarga al cambiar los filtros. `clave` es el campo del JSON con los datos.
export function useListado(url, filtros, clave = "alumnos") {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const f = useDebounce(JSON.stringify(filtros), 300);
  const n = useRef(0);

  const cargar = useCallback(async () => {
    const mio = ++n.current;
    setCargando(true);
    try {
      const params = {};
      Object.entries(JSON.parse(f)).forEach(([k, v]) => { if (v !== "" && v != null) params[k] = v; });
      const { data } = await api.get(url, { params });
      if (mio === n.current) { setDatos(data[clave] || []); setError(""); }
    } catch (e) {
      if (mio === n.current) setError(msgError(e, "No se pudo cargar la lista"));
    } finally {
      if (mio === n.current) setCargando(false);
    }
  }, [url, f, clave]);

  useEffect(() => { cargar(); }, [cargar]);
  return { datos, cargando, error, recargar: cargar, setDatos };
}

export function estadoCita(a) {
  if (a.id_cita) return { tipo: "cita", texto: `${fechaES(a.cita_fecha).slice(0, 5)} · ${a.cita_hora}`, clase: "badge-green" };
  if (a.id_fh) return { tipo: "fh", texto: `Fuera de hora · ${fechaES(a.fh_fecha).slice(0, 5)}`, clase: "badge-amber" };
  return { tipo: "nada", texto: "Sin fecha", clase: "badge-gray" };
}
